import React, { useEffect, useMemo } from "react";

import {
  Button,
  ArrowButton,
  Typography,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { useTypedSelector } from "../app/rootReducer";
import { useAppDispatch } from "../app/store";
import { ValidationFailure } from "../models";
import styles from "./Toolbar.module.scss";
import { resetSaveOK } from "../slices/applicationSlice";

export interface ToolbarTopProps {
  children?: any;
  pageTitle?: string;
  pageSubTitle?: string;
  showBack?: "hide" | "disable" | "show";
  showAdd?: "hide" | "disable" | "show";
  showEdit?: "hide" | "disable" | "show";
  showSave?: "hide" | "disable" | "show";
  showCancel?: "hide" | "disable" | "show";
  showCustom?: boolean;
  isEdited?: boolean;
  errors?: ValidationFailure[];
  onClickAdd?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickEdit?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickBack?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickSave?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickCancel?: (event: React.MouseEvent<any, MouseEvent>) => void;
}
export const ToolbarTop = ({
  children,
  pageTitle,
  pageSubTitle,
  showBack,
  showAdd,
  showEdit,
  showSave,
  showCancel,
  showCustom,
  errors,
  onClickAdd,
  onClickEdit,
  onClickSave,
  onClickCancel,
  onClickBack,
}: ToolbarTopProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const saveOK: boolean = useTypedSelector((state) => state.application.saveOK);

  const renderAddButton = useMemo(() => {
    switch (showAdd) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            onClick={onClickAdd}
          >
            {t("common.addNew")}
          </Button>
        );
      case "disable":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            disabled={true}
          >
            {t("common.addNew")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showAdd, onClickAdd]);

  const renderEditButton = useMemo(() => {
    switch (showEdit) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            onClick={onClickEdit}
          >
            {t("common.edit")}
          </Button>
        );
      case "disable":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            disabled={true}
          >
            {t("common.edit")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showEdit, onClickEdit]);

  const renderBackButton = () => {
    switch (showBack) {
      case "show":
        return (
          <>
            <ArrowButton
              ariaLabel={t("accessibility.backButton")}
              backwards
              onClick={onClickBack ? onClickBack : () => history.goBack()}
              title={t("accessibility.backButton")}
            ></ArrowButton>
            <Typography
              variant="base"
              component="span"
              color="primary"
              className="mb-0"
            >
              {t("common.back")}
            </Typography>
          </>
        );
      case "disable":
        return <></>;
      default:
        return <></>;
    }
  };

  const renderSaveButton = useMemo(() => {
    switch (showSave) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            onClick={onClickSave}
          >
            {t("common.save")}
          </Button>
        );
      case "disable":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            disabled={true}
          >
            {t("common.save")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showSave, onClickSave]);

  const renderCancelButton = useMemo(() => {
    switch (showCancel) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            onClick={onClickCancel}
          >
            {t("common.cancel")}
          </Button>
        );
      case "disable":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            disabled={true}
          >
            {t("common.cancel")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showCancel, onClickCancel]);

  useEffect(() => {
    if (saveOK) dispatch(resetSaveOK());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCustomButton = () => (showCustom ? <>{children}</> : <></>);

  const renderInfoSuccess = useMemo(() => {
    if (saveOK) {
      window.scroll(0, 0);
      return (
        <InfoPanel
          color="success"
          fullWidth={true}
          message={t("common.saveSuccess")}
          timeoutSec={3}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const renderInfoError = useMemo(() => {
    if (errors !== undefined && errors.length > 0) {
      window.scroll(0, 0);
      return (
        <InfoPanel
          color="danger"
          fullWidth={true}
          dismissible={true}
          message=""
        >
          <b>Greška kod snimanja podataka</b>
          <br></br>
          <ul>
            {errors.map((error) => (
              <li>{error.message}</li>
            ))}
          </ul>
        </InfoPanel>
      );
    }
  }, [errors]);

  const renderSaveAndCancelButtons = () => {
    return (
      <div className="d-flex">
        {renderSaveButton}
        {renderCancelButton}
      </div>
    );
  };

  return (
    <>
      <div className={styles.toolbar}>
        <Row className="pl-0">
          <Col lg="3">
            <Col lg="12" className="pl-2">
              {renderBackButton()}
            </Col>
          </Col>
          <Col lg="6" className="pl-0">
            {renderInfoSuccess}
            {renderInfoError}
          </Col>
          <Col lg="3">
            <Col lg="12">
              {renderCustomButton()}
              {renderAddButton}
              {renderEditButton}
              {renderCancelButton}
            </Col>
          </Col>
          <Col lg="12">{renderSaveAndCancelButtons()}</Col>
        </Row>
        <Row>
          <Col lg="12">
            <Typography variant="mediumBold" className="mb-0 text-uppercase">
              {pageTitle}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col lg="12">
            <Typography variant="mediumBold" className="mb-0 text-uppercase">
              {pageSubTitle}
            </Typography>
          </Col>
        </Row>
      </div>
    </>
  );
};
